/**
 * The design system: six palettes, four type pairings, six backgrounds.
 *
 * 144 combinations, every one of them vetted. The constraint IS the product --
 * a founder who cannot pick a bad combination cannot spend three days picking.
 * Adding a knob here is a product decision, not a styling one.
 */

export type PaletteId =
  | 'juno' | 'vacuum' | 'solstice' | 'terminal'
  | 'bloom' | 'tidal' | 'ember' | 'arctic'
export type FontPairId =
  | 'inter' | 'editorial' | 'technical' | 'mono'
  | 'display' | 'geometric' | 'grotesque' | 'classic'
export type BackgroundId =
  | 'solid' | 'mesh' | 'blueprint' | 'starfield'
  | 'rings' | 'dawn' | 'launch' | 'orbit'

export interface Palette {
  readonly id: PaletteId
  readonly label: string
  /** Page ground. */
  readonly bg: string
  /** Raised surfaces: input fields, the scrim behind text on photos. */
  readonly surface: string
  readonly text: string
  readonly muted: string
  /** Countdown digits and the primary button. */
  readonly accent: string
  /** Text placed on top of `accent`. */
  readonly accentText: string
  readonly line: string
  /** Drives `color-scheme` and the direction of the photo scrim. */
  readonly scheme: 'dark' | 'light'
}

/**
 * "Juno" is read from the reference: navy blueprint ground, vermilion ignition.
 * The rest deliberately span warm/cool and light/dark so the six do not feel
 * like one idea recoloured.
 */
export const PALETTES: Readonly<Record<PaletteId, Palette>> = {
  juno: {
    id: 'juno',
    label: 'Juno',
    bg: '#0A2A43',
    surface: '#12496B',
    text: '#F5F7F9',
    muted: '#A8C4D8',
    accent: '#F4703A',
    accentText: '#1A0A02',
    line: 'rgba(245,247,249,0.12)',
    scheme: 'dark',
  },
  vacuum: {
    id: 'vacuum',
    label: 'Vacuum',
    bg: '#08090A',
    surface: '#16191C',
    text: '#FAFAFA',
    muted: '#9CA3A9',
    accent: '#22D3EE',
    accentText: '#04141A',
    line: 'rgba(250,250,250,0.12)',
    scheme: 'dark',
  },
  solstice: {
    id: 'solstice',
    label: 'Solstice',
    bg: '#FAF4E8',
    surface: '#F0E4CE',
    text: '#2E1F12',
    muted: '#6B5741',
    accent: '#B23C0A',
    accentText: '#FFFFFF',
    line: 'rgba(46,31,18,0.14)',
    scheme: 'light',
  },
  terminal: {
    id: 'terminal',
    label: 'Terminal',
    bg: '#000000',
    surface: '#0B160B',
    text: '#C8F7C5',
    muted: '#6FA86B',
    accent: '#3BE06B',
    accentText: '#02140A',
    line: 'rgba(59,224,107,0.20)',
    scheme: 'dark',
  },
  bloom: {
    id: 'bloom',
    label: 'Bloom',
    bg: '#FBFAFC',
    surface: '#F1EEF6',
    text: '#17131C',
    muted: '#635B6E',
    accent: '#A21CAF',
    accentText: '#FFFFFF',
    line: 'rgba(23,19,28,0.12)',
    scheme: 'light',
  },
  tidal: {
    id: 'tidal',
    label: 'Tidal',
    bg: '#06312F',
    surface: '#0C4A46',
    text: '#F1EDE4',
    muted: '#9EC0BB',
    accent: '#F0BC55',
    accentText: '#04211F',
    line: 'rgba(241,237,228,0.12)',
    scheme: 'dark',
  },
  ember: {
    id: 'ember',
    label: 'Ember',
    bg: '#1A0F0B',
    surface: '#2C1A12',
    text: '#F6EADF',
    muted: '#BFA08C',
    accent: '#FF8A4C',
    accentText: '#1A0F0B',
    line: 'rgba(246,234,223,0.12)',
    scheme: 'dark',
  },
  arctic: {
    id: 'arctic',
    label: 'Arctic',
    bg: '#F4F7FA',
    surface: '#E4EBF2',
    text: '#111C27',
    muted: '#566577',
    accent: '#1D4ED8',
    accentText: '#FFFFFF',
    line: 'rgba(17,28,39,0.12)',
    scheme: 'light',
  },
}

export interface FontPair {
  readonly id: FontPairId
  readonly label: string
  /** Family stack for the wordmark and countdown. */
  readonly display: string
  readonly body: string
  /** Families to load from the self-hosted /f/ directory. */
  readonly families: readonly string[]
  readonly displayWeight: number
  /** Wordmark letter-spacing, in em. Serif and mono want less. */
  readonly displayTracking: string
}

const SANS_FALLBACK = 'system-ui, -apple-system, "Segoe UI", Roboto, sans-serif'
const SERIF_FALLBACK = 'Georgia, "Times New Roman", serif'
const MONO_FALLBACK = 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace'

export const FONT_PAIRS: Readonly<Record<FontPairId, FontPair>> = {
  inter: {
    id: 'inter',
    label: 'Neutral',
    display: `"InterTight", ${SANS_FALLBACK}`,
    body: `"Inter", ${SANS_FALLBACK}`,
    families: ['InterTight', 'Inter'],
    displayWeight: 700,
    displayTracking: '-0.03em',
  },
  editorial: {
    id: 'editorial',
    label: 'Editorial',
    display: `"InstrumentSerif", ${SERIF_FALLBACK}`,
    body: `"Inter", ${SANS_FALLBACK}`,
    families: ['InstrumentSerif', 'Inter'],
    displayWeight: 400,
    displayTracking: '-0.01em',
  },
  technical: {
    id: 'technical',
    label: 'Technical',
    display: `"SpaceGrotesk", ${SANS_FALLBACK}`,
    body: `"SpaceGrotesk", ${SANS_FALLBACK}`,
    families: ['SpaceGrotesk'],
    displayWeight: 700,
    displayTracking: '-0.02em',
  },
  mono: {
    id: 'mono',
    label: 'Terminal',
    display: `"JetBrainsMono", ${MONO_FALLBACK}`,
    body: `"Inter", ${SANS_FALLBACK}`,
    families: ['JetBrainsMono', 'Inter'],
    displayWeight: 700,
    displayTracking: '-0.04em',
  },
  display: {
    id: 'display',
    label: 'Display',
    display: `"Fraunces", ${SERIF_FALLBACK}`,
    body: `"Inter", ${SANS_FALLBACK}`,
    families: ['Fraunces', 'Inter'],
    displayWeight: 700,
    displayTracking: '-0.02em',
  },
  geometric: {
    id: 'geometric',
    label: 'Geometric',
    display: `"Sora", ${SANS_FALLBACK}`,
    body: `"Inter", ${SANS_FALLBACK}`,
    families: ['Sora', 'Inter'],
    displayWeight: 700,
    displayTracking: '-0.035em',
  },
  grotesque: {
    id: 'grotesque',
    label: 'Grotesque',
    display: `"BricolageGrotesque", ${SANS_FALLBACK}`,
    body: `"Inter", ${SANS_FALLBACK}`,
    families: ['BricolageGrotesque', 'Inter'],
    displayWeight: 800,
    displayTracking: '-0.03em',
  },
  classic: {
    id: 'classic',
    label: 'Classic',
    display: `"PlayfairDisplay", ${SERIF_FALLBACK}`,
    body: `"Inter", ${SANS_FALLBACK}`,
    families: ['PlayfairDisplay', 'Inter'],
    displayWeight: 700,
    displayTracking: '-0.01em',
  },
}

export interface Background {
  readonly id: BackgroundId
  readonly label: string
  /** True when the layer is a photograph and needs the heavy scrim. */
  readonly photographic: boolean
}

export const BACKGROUNDS: Readonly<Record<BackgroundId, Background>> = {
  solid: { id: 'solid', label: 'Solid', photographic: false },
  mesh: { id: 'mesh', label: 'Gradient', photographic: false },
  blueprint: { id: 'blueprint', label: 'Blueprint', photographic: false },
  starfield: { id: 'starfield', label: 'Starfield', photographic: false },
  rings: { id: 'rings', label: 'Rings', photographic: false },
  dawn: { id: 'dawn', label: 'Dawn', photographic: false },
  launch: { id: 'launch', label: 'Launch', photographic: true },
  orbit: { id: 'orbit', label: 'Earth', photographic: true },
}

export const PALETTE_IDS = Object.keys(PALETTES) as readonly PaletteId[]
export const FONT_PAIR_IDS = Object.keys(FONT_PAIRS) as readonly FontPairId[]
export const BACKGROUND_IDS = Object.keys(BACKGROUNDS) as readonly BackgroundId[]

export interface ThemeChoice {
  readonly palette: PaletteId
  readonly font: FontPairId
  readonly background: BackgroundId
}

export const DEFAULT_THEME: ThemeChoice = {
  palette: 'juno',
  font: 'inter',
  background: 'blueprint',
}

export function isThemeChoice(value: unknown): value is ThemeChoice {
  if (typeof value !== 'object' || value === null) return false
  const t = value as Record<string, unknown>
  return (
    typeof t['palette'] === 'string' && t['palette'] in PALETTES &&
    typeof t['font'] === 'string' && t['font'] in FONT_PAIRS &&
    typeof t['background'] === 'string' && t['background'] in BACKGROUNDS
  )
}
